import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Space, Table, TableColumnType, Tag } from "antd";
import { useState } from "react";
import ModalAddSupplier from "../../components/modal/ModalAddSupplier";
import ModalEditSupplier from "../../components/modal/ModalEditSupplier";
import { Currency, SupplierType } from "../../enum/common";
import { IPurchaseFilter, IPurchaseSupplier } from "../../models/Purchase";
import { useDeleteSupplier, useGetSuppliers } from "../../query/PurchaseQuery";
import { notify } from "../../utils/toast";

const PurchaseSupplier = () => {
  const [params, setParams] = useState<IPurchaseFilter>({
    page: 1,
    limit: 10,
  });

  const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [selectedSupplier, setSelectedSupplier] =
    useState<IPurchaseSupplier | null>(null);

  const { data, isLoading } = useGetSuppliers(params);
  const deleteSupplier = useDeleteSupplier();

  const handleDeleteSupplier = async (id: number) => {
    try {
      await deleteSupplier.mutateAsync({ id, query: params });
      notify.success("Xóa nhà cung cấp thành công");
    } catch (error) {
      notify.error("Xóa nhà cung cấp thất bại");
    }
  };

  const handleEditSupplier = (record: IPurchaseSupplier) => {
    setSelectedSupplier(record);
    setOpenModalEdit(true);
  };

  const getSupplierTypeColor = (type: SupplierType) => {
    switch (type) {
      case SupplierType.LOCAL:
        return "blue";
      case SupplierType.ONLINE:
        return "green";
      default:
        return "default";
    }
  };

  const getCurrencyColor = (currency: Currency) => {
    switch (currency) {
      case Currency.VND:
        return "red";
      case Currency.USD:
        return "green";
      case Currency.EUR:
        return "blue";
      case Currency.CNY:
        return "orange";
      default:
        return "default";
    }
  };

  const columns: TableColumnType<IPurchaseSupplier>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: 80,
    },
    {
      title: "Tên nhà cung cấp",
      dataIndex: "name",
      key: "name",
      align: "left",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "left",
    },
    {
      title: "Loại",
      dataIndex: "type",
      key: "type",
      align: "center",
      width: 120,
      render: (type: SupplierType) => (
        <Tag color={getSupplierTypeColor(type)}>
          {type === SupplierType.LOCAL ? "Nội địa" : "Trực tuyến"}
        </Tag>
      ),
    },
    {
      title: "Quốc gia",
      dataIndex: "country",
      key: "country",
      align: "center",
      width: 120,
    },
    {
      title: "Tiền tệ",
      dataIndex: "currency",
      key: "currency",
      align: "center",
      width: 100,
      render: (currency: Currency) => (
        <Tag color={getCurrencyColor(currency)}>{currency}</Tag>
      ),
    },
    {
      title: "Hành động",
      key: "action",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSupplier(record)}
          />
          <Popconfirm
            title="Xóa nhà cung cấp"
            description="Bạn có chắc chắn muốn xóa nhà cung cấp này?"
            onConfirm={() => handleDeleteSupplier(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenModalAdd(true)}
        >
          Thêm nhà cung cấp
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: data?.pagination.total,
          showSizeChanger: false,
          onChange: (page, pageSize) =>
            setParams((prev) => ({ ...prev, page, limit: pageSize })),
        }}
        scroll={{ x: 1000 }}
      />

      <ModalAddSupplier
        open={openModalAdd}
        onCancel={() => setOpenModalAdd(false)}
        onSuccess={() => {
          setOpenModalAdd(false);
        }}
      />

      <ModalEditSupplier
        open={openModalEdit}
        record={selectedSupplier}
        params={params}
        onCancel={() => {
          setOpenModalEdit(false);
          setSelectedSupplier(null);
        }}
        onSuccess={() => {
          setOpenModalEdit(false);
          setSelectedSupplier(null);
        }}
      />
    </div>
  );
};

export default PurchaseSupplier;
